import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/context/auth-context";
import { generateShopSSOToken } from "@/lib/pocketbase";
import { useShopBanner } from "@/hooks/useShopBanner";
import ShopPromoBanner from "./ShopPromoBanner";
import menu from "@/assets/svg_icons/menu.svg";
import close from "@/assets/svg_icons/close.svg";

interface INavbarProps {
  hideEnergy?: boolean;
}

const Navbar = ({}: INavbarProps) => {
  // TODO: Mobile menu state - restore when mobile menu is implemented
  const [open, setIsOpen] = useState(false);
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { isBannerVisible, dismissBanner } = useShopBanner();

  const handleGoToShop = async () => {
    try {
      setIsLoadingSSO(true);
      const redirectUrl = await generateShopSSOToken();
      window.open(redirectUrl, "_blank");
    } catch (error) {
      console.error("Failed to redirect to shop:", error);
      alert("Failed to redirect to shop. Please try again.");
    } finally {
      setIsLoadingSSO(false);
    }
  };

  const handleClickOutside = (_event: MouseEvent) => {
    // if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
    //   setIsOpen(false);
    // }
  };

  const handleOpenMobileMenu = () => {
    setIsOpen(true);
  };
  const handleCloseMobileMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {isBannerVisible && <ShopPromoBanner onDismiss={dismissBanner} />}
      <div className={`w-full flex justify-between px-7 z-50 bg-base-100 border-b-4 border-b-base-300 fixed navbar-container transition-all duration-300 ${isBannerVisible ? 'top-10' : 'top-0'}`}>
        <div className="relative w-full container m-auto">
          <div
            ref={menuRef}
            className="w-full py-3 lg:py-7 flex items-center justify-between"
          >
            <div className="flex gap-y-[10px]">
              <Link to="/" className="text-[32px] font-bold me-3 md lg:me-10">
                Booq
              </Link>

              <div className="gap-y-[10px] hidden lg:flex">
                <Link
                  to="/dashboard"
                  className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="19"
                    viewBox="0 0 20 19"
                    fill="none"
                    className="me-2"
                  >
                    <path
                      d="M0.789062 2C0.789062 0.89543 1.68449 0 2.78906 0H7.78906C8.89363 0 9.78906 0.895431 9.78906 2V8C9.78906 9.10457 8.89363 10 7.78906 10H2.78906C1.68449 10 0.789062 9.10457 0.789062 8V2Z"
                      fill="white"
                    />
                    <path
                      d="M7.78906 8.7998V10H2.78906V8.7998H7.78906ZM8.58887 8V2C8.58887 1.55817 8.23089 1.2002 7.78906 1.2002H2.78906C2.34723 1.2002 1.98926 1.55817 1.98926 2V8C1.98926 8.44183 2.34723 8.7998 2.78906 8.7998V10L2.58496 9.98926C1.64341 9.8938 0.895262 9.14565 0.799805 8.2041L0.789062 8V2C0.789062 0.895431 1.68449 8.05319e-09 2.78906 0H7.78906C8.89363 0 9.78906 0.895431 9.78906 2V8C9.78906 9.03565 9.00184 9.887 7.99316 9.98926L7.78906 10V8.7998C8.23089 8.7998 8.58887 8.44183 8.58887 8Z"
                      fill="#0b2c68"
                    />
                    <path
                      d="M10.7891 11C10.7891 9.89543 11.6845 9 12.7891 9H17.7891C18.8936 9 19.7891 9.89543 19.7891 11V17C19.7891 18.1046 18.8936 19 17.7891 19H12.7891C11.6845 19 10.7891 18.1046 10.7891 17V11Z"
                      fill="white"
                    />
                    <path
                      d="M17.7891 17.7998V19H12.7891V17.7998H17.7891ZM18.5889 17V11C18.5889 10.5582 18.2309 10.2002 17.7891 10.2002H12.7891C12.3472 10.2002 11.9893 10.5582 11.9893 11V17C11.9893 17.4418 12.3472 17.7998 12.7891 17.7998V19L12.585 18.9893C11.6434 18.8938 10.8953 18.1457 10.7998 17.2041L10.7891 17V11C10.7891 9.89543 11.6845 9 12.7891 9H17.7891C18.8936 9 19.7891 9.89543 19.7891 11V17C19.7891 18.0357 19.0018 18.887 17.9932 18.9893L17.7891 19V17.7998C18.2309 17.7998 18.5889 17.4418 18.5889 17Z"
                      fill="#0b2c68"
                    />
                    <path
                      d="M10.7891 2C10.7891 0.895431 11.6845 0 12.7891 0H17.7891C18.8936 0 19.7891 0.895431 19.7891 2V6C19.7891 7.10457 18.8936 8 17.7891 8H12.7891C11.6845 8 10.7891 7.10457 10.7891 6V2Z"
                      fill="white"
                    />
                    <path
                      d="M17.7891 6.7998V8H12.7891V6.7998H17.7891ZM18.5889 6V2C18.5889 1.55817 18.2309 1.2002 17.7891 1.2002H12.7891C12.3472 1.2002 11.9893 1.55817 11.9893 2V6C11.9893 6.44183 12.3472 6.7998 12.7891 6.7998V8L12.585 7.98926C11.5763 7.887 10.7891 7.03565 10.7891 6V2C10.7891 0.895431 11.6845 8.05319e-09 12.7891 0H17.7891C18.8936 0 19.7891 0.895431 19.7891 2V6L19.7783 6.2041C19.6829 7.14565 18.9347 7.8938 17.9932 7.98926L17.7891 8V6.7998C18.2309 6.7998 18.5889 6.44183 18.5889 6Z"
                      fill="#0b2c68"
                    />
                    <path
                      d="M0.789062 13C0.789062 11.8954 1.68449 11 2.78906 11H7.78906C8.89363 11 9.78906 11.8954 9.78906 13V17C9.78906 18.1046 8.89363 19 7.78906 19H2.78906C1.68449 19 0.789062 18.1046 0.789062 17V13Z"
                      fill="white"
                    />
                    <path
                      d="M7.78906 17.7998V19H2.78906V17.7998H7.78906ZM8.58887 17V13C8.58887 12.5582 8.23089 12.2002 7.78906 12.2002H2.78906C2.34723 12.2002 1.98926 12.5582 1.98926 13V17C1.98926 17.4418 2.34723 17.7998 2.78906 17.7998V19L2.58496 18.9893C1.57629 18.887 0.789062 18.0357 0.789062 17V13C0.789063 11.8954 1.68449 11 2.78906 11H7.78906C8.89363 11 9.78906 11.8954 9.78906 13V17L9.77832 17.2041C9.68286 18.1457 8.93472 18.8938 7.99316 18.9893L7.78906 19V17.7998C8.23089 17.7998 8.58887 17.4418 8.58887 17Z"
                      fill="#0b2c68"
                    />
                  </svg>
                  Dashboard
                </Link>
                {user?.role === "teacher" && (
                  <Link
                    to="/teacher-zone"
                    className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="me-2"
                    >
                      <path
                        d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
                        fill="white"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M3 20C3 16.134 6.13401 13 10 13C13.866 13 17 16.134 17 20"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M16 6L19 9M19 9L16 12M19 9H13"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Teacher Zone
                  </Link>
                )}
                {/* <Link
                  to="/leaderboard"
                  className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    className="me-2"
                  >
                    <path
                      d="M15.9139 14.0469C15.9101 14.0418 15.9059 14.0349 15.8993 14.0273C15.8551 13.9768 15.771 13.9079 15.6434 13.8369C15.3844 13.6929 15.0508 13.5996 14.7811 13.5996H12.1131V17.4004H15.5135C15.5925 17.4004 15.6244 17.3782 15.6561 17.3447C15.7046 17.2934 15.7628 17.1924 15.8114 17.0225C15.9099 16.6774 15.9139 16.2591 15.9139 16V14.0469ZM10.9139 11C10.9139 10.9152 10.8845 10.834 10.6913 10.7412C10.484 10.6417 10.2051 10.5996 10.0135 10.5996H7.51353C7.44293 10.5996 7.32353 10.6258 7.23912 10.6865C7.20296 10.7126 7.1758 10.7431 7.15611 10.7812C7.13666 10.8189 7.11314 10.8857 7.11314 11V17.4004H10.9139V11ZM2.11412 17.3008C2.14537 17.3108 2.18384 17.3254 2.23228 17.3359C2.39411 17.3711 2.64271 17.4004 3.01353 17.4004H5.91392V13.0996H2.54869C2.54067 13.1073 2.11412 13.2251 2.11412 14.5V17.3008ZM12.1131 12.4004H14.7811C15.2793 12.4004 15.8124 12.5572 16.2274 12.7881C16.4368 12.9046 16.642 13.0545 16.8026 13.2383C16.96 13.4185 17.1131 13.6783 17.1131 14V16C17.1131 16.2408 17.1171 16.8226 16.9657 17.3525C16.8893 17.6198 16.7599 17.9253 16.5272 18.1709C16.2777 18.4342 15.9344 18.5996 15.5135 18.5996H3.01353C2.18144 18.5996 1.64776 18.4757 1.31529 18.2383C1.13419 18.1089 1.01326 17.9455 0.953959 17.7676C0.925823 17.6831 0.914868 17.6051 0.912943 17.5391C0.912519 17.5242 0.913535 17.5096 0.91392 17.4961V14.5C0.91392 12.7875 1.66611 11.9006 2.51353 11.9004H5.91392V11C5.91392 10.4044 6.17511 9.97413 6.53794 9.71289C6.87016 9.47373 7.25087 9.40039 7.51353 9.40039H10.0135C10.322 9.40039 10.793 9.45866 11.2108 9.65918C11.6425 9.86642 12.1131 10.2849 12.1131 11V12.4004Z"
                      fill="#0b2c68"
                    />
                    <path
                      d="M8.08628 1.31054C8.42232 0.47453 9.60571 0.47453 9.94175 1.31054L10.3314 2.28124C10.4744 2.63732 10.809 2.88013 11.1917 2.90624L12.2357 2.97753C13.135 3.03851 13.5007 4.16424 12.8089 4.74218L12.0062 5.41308C11.7117 5.6592 11.5844 6.0526 11.6781 6.4248L11.933 7.43945C12.1389 8.25893 11.31 8.92186 10.5765 8.60644L10.432 8.53027L9.54526 7.97363C9.22037 7.76987 8.80766 7.76987 8.48276 7.97363L7.59605 8.53027L7.45151 8.60644C6.76686 8.90087 5.99839 8.34269 6.06675 7.60058L6.09507 7.43945L6.34995 6.4248C6.4319 6.09906 6.34489 5.75691 6.12437 5.51171L6.02183 5.41308L5.21909 4.74218C4.57052 4.2003 4.85133 3.17688 5.63023 3.00097L5.79233 2.97753L6.83628 2.90624C7.1714 2.88338 7.4692 2.69479 7.63413 2.40917L7.69663 2.28124L8.08628 1.31054ZM8.62437 2.65429C8.3383 3.36666 7.66955 3.85231 6.90366 3.90429L5.85972 3.97558L6.66245 4.6455C7.25159 5.13773 7.50698 5.92444 7.31968 6.66894L7.0648 7.68359L7.95054 7.12695C8.60073 6.71875 9.4273 6.71875 10.0775 7.12695L10.9632 7.68359L10.7084 6.66894C10.5211 5.92443 10.7764 5.13773 11.3656 4.6455L12.1683 3.97558L11.1244 3.90429C10.3585 3.85231 9.68973 3.36666 9.40366 2.65429L9.01401 1.68359L8.62437 2.65429Z"
                      fill="#0b2c68"
                    />
                    <path
                      d="M4.79126 7.41602L3.29126 8.41602L2.73657 7.58398L4.23657 6.58398L4.79126 7.41602ZM15.2913 7.58398L14.7366 8.41602L13.2366 7.41602L13.7913 6.58398L15.2913 7.58398ZM4.01392 4V5H2.01392V4H4.01392ZM16.0139 4V5H14.0139V4H16.0139ZM4.79126 1.58398L4.23657 2.41602L2.73657 1.41602L3.29126 0.583984L4.79126 1.58398ZM15.2913 1.41602L13.7913 2.41602L13.2366 1.58398L14.7366 0.583984L15.2913 1.41602Z"
                      fill="#0b2c68"
                    />
                  </svg>
                  Leaderboard
                </Link> */}
                <Link
                  to="/books/search"
                  className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                >
                  {/* <img src={dashboard} alt="" className="me-2" /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    className="me-2"
                  >
                    <path
                      d="M11.9087 0.921875C12.8455 0.311894 14.0713 -0.219247 15.3198 0.0927734L15.3364 0.0966797L15.353 0.101562C15.6331 0.189241 15.8802 0.306334 16.0835 0.472656C16.2954 0.646106 16.4339 0.852751 16.521 1.07812C16.5974 1.27617 16.63 1.48056 16.6509 1.66309C17.1354 1.62075 17.678 1.65542 18.1685 1.79785C18.9267 2.01814 19.7488 2.57032 19.7739 3.66113C19.8086 5.17058 19.7739 11.188 19.7739 12.6748C19.7739 13.0433 19.7741 13.412 19.7485 13.7441C19.7231 14.0746 19.6693 14.4164 19.5376 14.7236C19.3993 15.046 19.1758 15.3288 18.8325 15.5195C18.5024 15.7028 18.1091 15.7744 17.6733 15.7744H2.17432C1.73854 15.7744 1.34526 15.7029 1.01514 15.5195C0.671891 15.3288 0.448314 15.0461 0.310059 14.7236C0.178382 14.4164 0.124556 14.0746 0.0991211 13.7441C0.0735706 13.412 0.0737303 13.0433 0.0737305 12.6748C0.0737305 11.188 0.0390285 5.17058 0.0737305 3.66113C0.0988356 2.57032 0.920981 2.01814 1.6792 1.79785C2.16969 1.6554 2.71222 1.62074 3.19678 1.66309C3.21763 1.48056 3.25026 1.27616 3.32666 1.07812C3.41371 0.852751 3.55226 0.646106 3.76416 0.472656C3.96745 0.306311 4.21448 0.189248 4.49463 0.101562L4.51123 0.0966797L4.52783 0.0927734C5.77642 -0.219292 7.00213 0.31188 7.93896 0.921875C8.77359 1.46538 9.48119 2.14019 9.92334 2.57812C10.3654 2.14022 11.0738 1.46559 11.9087 0.921875ZM3.09717 2.85938C2.75648 2.82921 2.36 2.8498 2.01416 2.9502C1.49724 3.10038 1.28172 3.35 1.27393 3.68848C1.23963 5.17943 1.27393 11.1618 1.27393 12.6748C1.27393 13.056 1.2741 13.3753 1.29541 13.6523C1.31687 13.9309 1.35684 14.1209 1.4126 14.251C1.46181 14.3657 1.51981 14.4272 1.59814 14.4707C1.68991 14.5216 1.8605 14.5742 2.17432 14.5742H8.51904C8.08749 14.1753 7.48398 13.8291 6.7749 13.5439C5.64688 13.0904 4.40457 12.8505 3.61475 12.7715L3.07373 12.7178V4.6748C3.07373 3.9772 3.07328 3.37333 3.09717 2.85938ZM16.7505 2.85938C16.7744 3.37333 16.7739 3.9772 16.7739 4.6748V12.7178L16.2329 12.7715C15.4431 12.8505 14.2007 13.0904 13.0728 13.5439C12.3637 13.8291 11.7602 14.1753 11.3286 14.5742H17.6733C17.9871 14.5742 18.1577 14.5216 18.2495 14.4707C18.3278 14.4272 18.3859 14.3657 18.4351 14.251C18.4908 14.1209 18.5308 13.9309 18.5522 13.6523C18.5736 13.3753 18.5737 13.056 18.5737 12.6748C18.5737 11.1618 18.608 5.17943 18.5737 3.68848C18.5659 3.35 18.3504 3.10038 17.8335 2.9502C17.4877 2.84982 17.0912 2.82921 16.7505 2.85938ZM7.28369 1.92773C6.43384 1.3744 5.58069 1.07288 4.83252 1.25391C4.65675 1.31082 4.5686 1.36481 4.52393 1.40137C4.4863 1.43219 4.46546 1.46151 4.44678 1.50977C4.42285 1.57173 4.40278 1.66472 4.38525 1.82812C4.3767 1.90797 4.36921 1.99457 4.36084 2.09668C4.35266 2.19649 4.34299 2.30908 4.33057 2.42871C4.27441 2.96958 4.27393 3.68247 4.27393 4.6748V11.6465C5.11603 11.7725 6.20368 12.0207 7.22314 12.4307C8.07034 12.7714 8.94885 13.2546 9.57373 13.9316V3.92285L9.24951 3.59863C8.83096 3.18008 8.12047 2.47262 7.28369 1.92773ZM15.0151 1.25391C14.267 1.07292 13.4138 1.37442 12.564 1.92773C11.8342 2.40296 11.2007 3.00181 10.7729 3.4248L10.7739 3.42578V13.4707C11.3266 13.0279 11.984 12.6883 12.6245 12.4307C13.6439 12.0207 14.7316 11.7725 15.5737 11.6465V4.6748C15.5737 3.68246 15.5732 2.96958 15.5171 2.42871C15.5047 2.30908 15.495 2.1965 15.4868 2.09668C15.4784 1.99457 15.471 1.90798 15.4624 1.82812C15.4449 1.66469 15.4248 1.57173 15.4009 1.50977C15.3822 1.46151 15.3614 1.43219 15.3237 1.40137C15.279 1.36482 15.1908 1.31079 15.0151 1.25391Z"
                      fill="#0b2c68"
                    />
                  </svg>
                  Take a quiz on a book
                </Link>
                <Link
                  to="/articles"
                  className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    className="me-2"
                  >
                    <path
                      d="M18.2368 7.56158C18.2368 7.03258 18.2368 6.16777 18.2368 5.35204M10.7368 18.0616C8.90348 18.0616 4.93682 18.0616 3.73682 18.0616C2.23682 18.0616 1.23682 17.0616 1.23682 15.5616C1.23682 14.3756 1.23682 8.50105 1.23682 5.35204M1.23682 5.35204C1.23682 4.51829 1.23682 3.8756 1.23682 3.56158C1.23682 2.06158 2.23682 1.06158 3.23682 1.06158C4.23682 1.06158 14.2368 1.06158 15.7368 1.06158C17.2368 1.06158 18.2368 2.56158 18.2368 3.56158C18.2368 3.93838 18.2368 4.62576 18.2368 5.35204M1.23682 5.35204H18.2368M4.23682 8.56158H11.2368M4.23682 11.5616H11.2368M4.23682 15.0616H9.23682M14.1923 10.3246L14.8045 8.99836C15.041 8.4858 15.6547 8.27053 16.1596 8.52299L17.3699 9.12813C17.853 9.36968 18.057 9.9512 17.8306 10.4416L17.2368 11.7283M14.1923 10.3246L12.2368 14.5616L12.613 17.195C12.6677 17.5781 13.1182 17.7565 13.4203 17.5148L15.2368 16.0616L17.2368 11.7283M14.1923 10.3246L17.2368 11.7283"
                      stroke="#0b2c68"
                      strokeLinecap="round"
                    />
                    <circle cx="4.38682" cy="3.21158" r="0.65" fill="#0b2c68" />
                    <circle cx="6.38682" cy="3.21158" r="0.65" fill="#0b2c68" />
                    <circle cx="8.38682" cy="3.21158" r="0.65" fill="#0b2c68" />
                  </svg>
                  Read an Article
                </Link>
                {user?.access_shop !== false && (
                  <button
                    onClick={handleGoToShop}
                    disabled={isLoadingSSO}
                    className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="me-2"
                    >
                      <path
                        d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 6H21"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10"
                        stroke="#0b2c68"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {isLoadingSSO ? "Loading..." : "Shop"}
                  </button>
                )}
              </div>
            </div>

            <div className="items-center hidden lg:flex">
              <Link
                to="/profile"
                className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                onClick={() => setIsOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="21"
                  viewBox="0 0 17 21"
                  fill="none"
                  className="me-2"
                >
                  <path
                    d="M8.60752 12.5L8.60742 12.5C5.45287 12.4999 1.10742 12.3 1.10742 15.5C1.10742 18.7 5.77409 19.5 8.10742 19.5C10.6074 19.5 15.6074 18.8 15.6074 16C15.6074 12.5 11.6075 12.5 8.60752 12.5Z"
                    stroke="#0b2c68"
                    strokeWidth="1.7"
                  />
                  <circle
                    cx="8.10742"
                    cy="5"
                    r="4.1"
                    fill="white"
                    stroke="#0b2c68"
                    strokeWidth="1.8"
                  />
                </svg>
                Profile
              </Link>
              <button
                className="w-fit flex items-center font-bold"
                onClick={async () => {
                  await signOut();
                  setIsOpen(false);
                  navigate({ to: "/" });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="21"
                  viewBox="0 0 17 21"
                  fill="none"
                  className="me-2"
                >
                  <path
                    d="M1.41211 19.9739C1.41211 15.9739 4.41211 11.9739 8.91211 12.4739M8.91211 16.4739H15.9121M15.9121 16.4739L13.4121 13.9739M15.9121 16.4739L13.4121 18.9739"
                    stroke="#0b2c68"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="8.41211"
                    cy="5.47388"
                    r="4"
                    stroke="#0b2c68"
                    strokeWidth="2"
                  />
                </svg>
                Log out
              </button>
            </div>

            <button onClick={handleOpenMobileMenu} className="block lg:hidden">
              <img
                src={menu}
                width={50}
                height={50}
                alt="Mobile burger menu icon"
              />
            </button>

            <div
              className={`mobile-menu flex flex-col justify-between lg:hidden ${
                open && "open"
              }`}
            >
              <div>
                <div className="flex items-center justify-between">
                  <h2>Menu</h2>
                  <button onClick={handleCloseMobileMenu}>
                    <img
                      src={close}
                      width={50}
                      height={50}
                      alt="Mobile close menu icon"
                    />
                  </button>
                </div>
                <ul className="mobile-menu-list mt-4">
                  <li onClick={handleCloseMobileMenu}>
                    <Link
                      to="/dashboard"
                      className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="19"
                        viewBox="0 0 20 19"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M0.789062 2C0.789062 0.89543 1.68449 0 2.78906 0H7.78906C8.89363 0 9.78906 0.895431 9.78906 2V8C9.78906 9.10457 8.89363 10 7.78906 10H2.78906C1.68449 10 0.789062 9.10457 0.789062 8V2Z"
                          fill="white"
                        />
                        <path
                          d="M7.78906 8.7998V10H2.78906V8.7998H7.78906ZM8.58887 8V2C8.58887 1.55817 8.23089 1.2002 7.78906 1.2002H2.78906C2.34723 1.2002 1.98926 1.55817 1.98926 2V8C1.98926 8.44183 2.34723 8.7998 2.78906 8.7998V10L2.58496 9.98926C1.64341 9.8938 0.895262 9.14565 0.799805 8.2041L0.789062 8V2C0.789062 0.895431 1.68449 8.05319e-09 2.78906 0H7.78906C8.89363 0 9.78906 0.895431 9.78906 2V8C9.78906 9.03565 9.00184 9.887 7.99316 9.98926L7.78906 10V8.7998C8.23089 8.7998 8.58887 8.44183 8.58887 8Z"
                          fill="#0b2c68"
                        />
                        <path
                          d="M10.7891 11C10.7891 9.89543 11.6845 9 12.7891 9H17.7891C18.8936 9 19.7891 9.89543 19.7891 11V17C19.7891 18.1046 18.8936 19 17.7891 19H12.7891C11.6845 19 10.7891 18.1046 10.7891 17V11Z"
                          fill="white"
                        />
                        <path
                          d="M17.7891 17.7998V19H12.7891V17.7998H17.7891ZM18.5889 17V11C18.5889 10.5582 18.2309 10.2002 17.7891 10.2002H12.7891C12.3472 10.2002 11.9893 10.5582 11.9893 11V17C11.9893 17.4418 12.3472 17.7998 12.7891 17.7998V19L12.585 18.9893C11.6434 18.8938 10.8953 18.1457 10.7998 17.2041L10.7891 17V11C10.7891 9.89543 11.6845 9 12.7891 9H17.7891C18.8936 9 19.7891 9.89543 19.7891 11V17C19.7891 18.0357 19.0018 18.887 17.9932 18.9893L17.7891 19V17.7998C18.2309 17.7998 18.5889 17.4418 18.5889 17Z"
                          fill="#0b2c68"
                        />
                        <path
                          d="M10.7891 2C10.7891 0.895431 11.6845 0 12.7891 0H17.7891C18.8936 0 19.7891 0.895431 19.7891 2V6C19.7891 7.10457 18.8936 8 17.7891 8H12.7891C11.6845 8 10.7891 7.10457 10.7891 6V2Z"
                          fill="white"
                        />
                        <path
                          d="M17.7891 6.7998V8H12.7891V6.7998H17.7891ZM18.5889 6V2C18.5889 1.55817 18.2309 1.2002 17.7891 1.2002H12.7891C12.3472 1.2002 11.9893 1.55817 11.9893 2V6C11.9893 6.44183 12.3472 6.7998 12.7891 6.7998V8L12.585 7.98926C11.5763 7.887 10.7891 7.03565 10.7891 6V2C10.7891 0.895431 11.6845 8.05319e-09 12.7891 0H17.7891C18.8936 0 19.7891 0.895431 19.7891 2V6L19.7783 6.2041C19.6829 7.14565 18.9347 7.8938 17.9932 7.98926L17.7891 8V6.7998C18.2309 6.7998 18.5889 6.44183 18.5889 6Z"
                          fill="#0b2c68"
                        />
                        <path
                          d="M0.789062 13C0.789062 11.8954 1.68449 11 2.78906 11H7.78906C8.89363 11 9.78906 11.8954 9.78906 13V17C9.78906 18.1046 8.89363 19 7.78906 19H2.78906C1.68449 19 0.789062 18.1046 0.789062 17V13Z"
                          fill="white"
                        />
                        <path
                          d="M7.78906 17.7998V19H2.78906V17.7998H7.78906ZM8.58887 17V13C8.58887 12.5582 8.23089 12.2002 7.78906 12.2002H2.78906C2.34723 12.2002 1.98926 12.5582 1.98926 13V17C1.98926 17.4418 2.34723 17.7998 2.78906 17.7998V19L2.58496 18.9893C1.57629 18.887 0.789062 18.0357 0.789062 17V13C0.789063 11.8954 1.68449 11 2.78906 11H7.78906C8.89363 11 9.78906 11.8954 9.78906 13V17L9.77832 17.2041C9.68286 18.1457 8.93472 18.8938 7.99316 18.9893L7.78906 19V17.7998C8.23089 17.7998 8.58887 17.4418 8.58887 17Z"
                          fill="#0b2c68"
                        />
                      </svg>
                      Dashboard
                    </Link>
                  </li>

                  {user?.role === "teacher" && (
                    <li onClick={handleCloseMobileMenu}>
                      <Link
                        to="/teacher-zone"
                        className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          className="me-2"
                        >
                          <path
                            d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
                            fill="white"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M3 20C3 16.134 6.13401 13 10 13C13.866 13 17 16.134 17 20"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M16 6L19 9M19 9L16 12M19 9H13"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Teacher Zone
                      </Link>
                    </li>
                  )}

                  {/* <li onClick={handleCloseMobileMenu}>
                    <Link
                      to="/leaderboard"
                      className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="19"
                        viewBox="0 0 18 19"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M15.9139 14.0469C15.9101 14.0418 15.9059 14.0349 15.8993 14.0273C15.8551 13.9768 15.771 13.9079 15.6434 13.8369C15.3844 13.6929 15.0508 13.5996 14.7811 13.5996H12.1131V17.4004H15.5135C15.5925 17.4004 15.6244 17.3782 15.6561 17.3447C15.7046 17.2934 15.7628 17.1924 15.8114 17.0225C15.9099 16.6774 15.9139 16.2591 15.9139 16V14.0469ZM10.9139 11C10.9139 10.9152 10.8845 10.834 10.6913 10.7412C10.484 10.6417 10.2051 10.5996 10.0135 10.5996H7.51353C7.44293 10.5996 7.32353 10.6258 7.23912 10.6865C7.20296 10.7126 7.1758 10.7431 7.15611 10.7812C7.13666 10.8189 7.11314 10.8857 7.11314 11V17.4004H10.9139V11ZM2.11412 17.3008C2.14537 17.3108 2.18384 17.3254 2.23228 17.3359C2.39411 17.3711 2.64271 17.4004 3.01353 17.4004H5.91392V13.0996H2.54869C2.54067 13.1073 2.11412 13.2251 2.11412 14.5V17.3008ZM12.1131 12.4004H14.7811C15.2793 12.4004 15.8124 12.5572 16.2274 12.7881C16.4368 12.9046 16.642 13.0545 16.8026 13.2383C16.96 13.4185 17.1131 13.6783 17.1131 14V16C17.1131 16.2408 17.1171 16.8226 16.9657 17.3525C16.8893 17.6198 16.7599 17.9253 16.5272 18.1709C16.2777 18.4342 15.9344 18.5996 15.5135 18.5996H3.01353C2.18144 18.5996 1.64776 18.4757 1.31529 18.2383C1.13419 18.1089 1.01326 17.9455 0.953959 17.7676C0.925823 17.6831 0.914868 17.6051 0.912943 17.5391C0.912519 17.5242 0.913535 17.5096 0.91392 17.4961V14.5C0.91392 12.7875 1.66611 11.9006 2.51353 11.9004H5.91392V11C5.91392 10.4044 6.17511 9.97413 6.53794 9.71289C6.87016 9.47373 7.25087 9.40039 7.51353 9.40039H10.0135C10.322 9.40039 10.793 9.45866 11.2108 9.65918C11.6425 9.86642 12.1131 10.2849 12.1131 11V12.4004Z"
                          fill="#0b2c68"
                        />
                        <path
                          d="M8.08628 1.31054C8.42232 0.47453 9.60571 0.47453 9.94175 1.31054L10.3314 2.28124C10.4744 2.63732 10.809 2.88013 11.1917 2.90624L12.2357 2.97753C13.135 3.03851 13.5007 4.16424 12.8089 4.74218L12.0062 5.41308C11.7117 5.6592 11.5844 6.0526 11.6781 6.4248L11.933 7.43945C12.1389 8.25893 11.31 8.92186 10.5765 8.60644L10.432 8.53027L9.54526 7.97363C9.22037 7.76987 8.80766 7.76987 8.48276 7.97363L7.59605 8.53027L7.45151 8.60644C6.76686 8.90087 5.99839 8.34269 6.06675 7.60058L6.09507 7.43945L6.34995 6.4248C6.4319 6.09906 6.34489 5.75691 6.12437 5.51171L6.02183 5.41308L5.21909 4.74218C4.57052 4.2003 4.85133 3.17688 5.63023 3.00097L5.79233 2.97753L6.83628 2.90624C7.1714 2.88338 7.4692 2.69479 7.63413 2.40917L7.69663 2.28124L8.08628 1.31054ZM8.62437 2.65429C8.3383 3.36666 7.66955 3.85231 6.90366 3.90429L5.85972 3.97558L6.66245 4.6455C7.25159 5.13773 7.50698 5.92444 7.31968 6.66894L7.0648 7.68359L7.95054 7.12695C8.60073 6.71875 9.4273 6.71875 10.0775 7.12695L10.9632 7.68359L10.7084 6.66894C10.5211 5.92443 10.7764 5.13773 11.3656 4.6455L12.1683 3.97558L11.1244 3.90429C10.3585 3.85231 9.68973 3.36666 9.40366 2.65429L9.01401 1.68359L8.62437 2.65429Z"
                          fill="#0b2c68"
                        />
                        <path
                          d="M4.79126 7.41602L3.29126 8.41602L2.73657 7.58398L4.23657 6.58398L4.79126 7.41602ZM15.2913 7.58398L14.7366 8.41602L13.2366 7.41602L13.7913 6.58398L15.2913 7.58398ZM4.01392 4V5H2.01392V4H4.01392ZM16.0139 4V5H14.0139V4H16.0139ZM4.79126 1.58398L4.23657 2.41602L2.73657 1.41602L3.29126 0.583984L4.79126 1.58398ZM15.2913 1.41602L13.7913 2.41602L13.2366 1.58398L14.7366 0.583984L15.2913 1.41602Z"
                          fill="#0b2c68"
                        />
                      </svg>
                      Leaderboard
                    </Link>
                  </li> */}

                  <li onClick={handleCloseMobileMenu}>
                    <Link
                      to="/books/search"
                      className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                    >
                      {/* <img src={dashboard} alt="" className="me-2" /> */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="16"
                        viewBox="0 0 20 16"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M11.9087 0.921875C12.8455 0.311894 14.0713 -0.219247 15.3198 0.0927734L15.3364 0.0966797L15.353 0.101562C15.6331 0.189241 15.8802 0.306334 16.0835 0.472656C16.2954 0.646106 16.4339 0.852751 16.521 1.07812C16.5974 1.27617 16.63 1.48056 16.6509 1.66309C17.1354 1.62075 17.678 1.65542 18.1685 1.79785C18.9267 2.01814 19.7488 2.57032 19.7739 3.66113C19.8086 5.17058 19.7739 11.188 19.7739 12.6748C19.7739 13.0433 19.7741 13.412 19.7485 13.7441C19.7231 14.0746 19.6693 14.4164 19.5376 14.7236C19.3993 15.046 19.1758 15.3288 18.8325 15.5195C18.5024 15.7028 18.1091 15.7744 17.6733 15.7744H2.17432C1.73854 15.7744 1.34526 15.7029 1.01514 15.5195C0.671891 15.3288 0.448314 15.0461 0.310059 14.7236C0.178382 14.4164 0.124556 14.0746 0.0991211 13.7441C0.0735706 13.412 0.0737303 13.0433 0.0737305 12.6748C0.0737305 11.188 0.0390285 5.17058 0.0737305 3.66113C0.0988356 2.57032 0.920981 2.01814 1.6792 1.79785C2.16969 1.6554 2.71222 1.62074 3.19678 1.66309C3.21763 1.48056 3.25026 1.27616 3.32666 1.07812C3.41371 0.852751 3.55226 0.646106 3.76416 0.472656C3.96745 0.306311 4.21448 0.189248 4.49463 0.101562L4.51123 0.0966797L4.52783 0.0927734C5.77642 -0.219292 7.00213 0.31188 7.93896 0.921875C8.77359 1.46538 9.48119 2.14019 9.92334 2.57812C10.3654 2.14022 11.0738 1.46559 11.9087 0.921875ZM3.09717 2.85938C2.75648 2.82921 2.36 2.8498 2.01416 2.9502C1.49724 3.10038 1.28172 3.35 1.27393 3.68848C1.23963 5.17943 1.27393 11.1618 1.27393 12.6748C1.27393 13.056 1.2741 13.3753 1.29541 13.6523C1.31687 13.9309 1.35684 14.1209 1.4126 14.251C1.46181 14.3657 1.51981 14.4272 1.59814 14.4707C1.68991 14.5216 1.8605 14.5742 2.17432 14.5742H8.51904C8.08749 14.1753 7.48398 13.8291 6.7749 13.5439C5.64688 13.0904 4.40457 12.8505 3.61475 12.7715L3.07373 12.7178V4.6748C3.07373 3.9772 3.07328 3.37333 3.09717 2.85938ZM16.7505 2.85938C16.7744 3.37333 16.7739 3.9772 16.7739 4.6748V12.7178L16.2329 12.7715C15.4431 12.8505 14.2007 13.0904 13.0728 13.5439C12.3637 13.8291 11.7602 14.1753 11.3286 14.5742H17.6733C17.9871 14.5742 18.1577 14.5216 18.2495 14.4707C18.3278 14.4272 18.3859 14.3657 18.4351 14.251C18.4908 14.1209 18.5308 13.9309 18.5522 13.6523C18.5736 13.3753 18.5737 13.056 18.5737 12.6748C18.5737 11.1618 18.608 5.17943 18.5737 3.68848C18.5659 3.35 18.3504 3.10038 17.8335 2.9502C17.4877 2.84982 17.0912 2.82921 16.7505 2.85938ZM7.28369 1.92773C6.43384 1.3744 5.58069 1.07288 4.83252 1.25391C4.65675 1.31082 4.5686 1.36481 4.52393 1.40137C4.4863 1.43219 4.46546 1.46151 4.44678 1.50977C4.42285 1.57173 4.40278 1.66472 4.38525 1.82812C4.3767 1.90797 4.36921 1.99457 4.36084 2.09668C4.35266 2.19649 4.34299 2.30908 4.33057 2.42871C4.27441 2.96958 4.27393 3.68247 4.27393 4.6748V11.6465C5.11603 11.7725 6.20368 12.0207 7.22314 12.4307C8.07034 12.7714 8.94885 13.2546 9.57373 13.9316V3.92285L9.24951 3.59863C8.83096 3.18008 8.12047 2.47262 7.28369 1.92773ZM15.0151 1.25391C14.267 1.07292 13.4138 1.37442 12.564 1.92773C11.8342 2.40296 11.2007 3.00181 10.7729 3.4248L10.7739 3.42578V13.4707C11.3266 13.0279 11.984 12.6883 12.6245 12.4307C13.6439 12.0207 14.7316 11.7725 15.5737 11.6465V4.6748C15.5737 3.68246 15.5732 2.96958 15.5171 2.42871C15.5047 2.30908 15.495 2.1965 15.4868 2.09668C15.4784 1.99457 15.471 1.90798 15.4624 1.82812C15.4449 1.66469 15.4248 1.57173 15.4009 1.50977C15.3822 1.46151 15.3614 1.43219 15.3237 1.40137C15.279 1.36482 15.1908 1.31079 15.0151 1.25391Z"
                          fill="#0b2c68"
                        />
                      </svg>
                      Take a quiz on a book
                    </Link>
                  </li>

                  <li onClick={handleCloseMobileMenu}>
                    <Link
                      to="/articles"
                      className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M18.2368 7.56158C18.2368 7.03258 18.2368 6.16777 18.2368 5.35204M10.7368 18.0616C8.90348 18.0616 4.93682 18.0616 3.73682 18.0616C2.23682 18.0616 1.23682 17.0616 1.23682 15.5616C1.23682 14.3756 1.23682 8.50105 1.23682 5.35204M1.23682 5.35204C1.23682 4.51829 1.23682 3.8756 1.23682 3.56158C1.23682 2.06158 2.23682 1.06158 3.23682 1.06158C4.23682 1.06158 14.2368 1.06158 15.7368 1.06158C17.2368 1.06158 18.2368 2.56158 18.2368 3.56158C18.2368 3.93838 18.2368 4.62576 18.2368 5.35204M1.23682 5.35204H18.2368M4.23682 8.56158H11.2368M4.23682 11.5616H11.2368M4.23682 15.0616H9.23682M14.1923 10.3246L14.8045 8.99836C15.041 8.4858 15.6547 8.27053 16.1596 8.52299L17.3699 9.12813C17.853 9.36968 18.057 9.9512 17.8306 10.4416L17.2368 11.7283M14.1923 10.3246L12.2368 14.5616L12.613 17.195C12.6677 17.5781 13.1182 17.7565 13.4203 17.5148L15.2368 16.0616L17.2368 11.7283M14.1923 10.3246L17.2368 11.7283"
                          stroke="#0b2c68"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="4.38682"
                          cy="3.21158"
                          r="0.65"
                          fill="#0b2c68"
                        />
                        <circle
                          cx="6.38682"
                          cy="3.21158"
                          r="0.65"
                          fill="#0b2c68"
                        />
                        <circle
                          cx="8.38682"
                          cy="3.21158"
                          r="0.65"
                          fill="#0b2c68"
                        />
                      </svg>
                      Read an Article
                    </Link>
                  </li>

                  {user?.access_shop !== false && (
                    <li onClick={handleCloseMobileMenu}>
                      <button
                        onClick={handleGoToShop}
                        disabled={isLoadingSSO}
                        className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="me-2"
                        >
                          <path
                            d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3 6H21"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10"
                            stroke="#0b2c68"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {isLoadingSSO ? "Loading..." : "Shop"}
                      </button>
                    </li>
                  )}
                  <li>
                    <Link
                      to="/profile"
                      className="text-[18px] w-fit text-action-500 flex items-center me-6 font-bold"
                      onClick={() => setIsOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="21"
                        viewBox="0 0 17 21"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M8.60752 12.5L8.60742 12.5C5.45287 12.4999 1.10742 12.3 1.10742 15.5C1.10742 18.7 5.77409 19.5 8.10742 19.5C10.6074 19.5 15.6074 18.8 15.6074 16C15.6074 12.5 11.6075 12.5 8.60752 12.5Z"
                          stroke="#0b2c68"
                          strokeWidth="1.7"
                        />
                        <circle
                          cx="8.10742"
                          cy="5"
                          r="4.1"
                          fill="white"
                          stroke="#0b2c68"
                          strokeWidth="1.8"
                        />
                      </svg>
                      Profile
                    </Link>
                  </li>
                  <li>
                    <button
                      className="w-fit flex items-center font-bold !color-red"
                      onClick={async () => {
                        await signOut();
                        setIsOpen(false);
                        navigate({ to: "/" });
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="21"
                        viewBox="0 0 17 21"
                        fill="none"
                        className="me-2"
                      >
                        <path
                          d="M1.41211 19.9739C1.41211 15.9739 4.41211 11.9739 8.91211 12.4739M8.91211 16.4739H15.9121M15.9121 16.4739L13.4121 13.9739M15.9121 16.4739L13.4121 18.9739"
                          stroke="#dd2525"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="8.41211"
                          cy="5.47388"
                          r="4"
                          stroke="#dd2525"
                          strokeWidth="2"
                        />
                      </svg>
                      Log out
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
